// UC71 — SYSTEM_ADMIN: MConnect (RSUD) events consumer log.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudTextInput,
  MudModal,
  MudInfoBox,
  MudSelect,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { useCriteriaParams } from '../../app/use-criteria-params';
import { QueryState } from '../../components/QueryState/QueryState';
import { rsudStatutLabel } from '../../features/registrars/api';
import {
  EmptyRegisterMessage,
  FilterBar,
  formatDateTime,
  PageHeader,
  onModalDismiss,
  useToast,
  type ActiveFilter,
} from '@simsm/ui-shared';

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
}

type Filter = 'all' | 'applied' | 'skipped';

const FILTERS: readonly Filter[] = ['all', 'applied', 'skipped'];

// ACCS-13: the outcome filter is a search criterion, so it lives in the address;
// `all` is the default and stays out of it. per docs/shape-ops-readable-urls-and-history.md §2
function readFilter(value: string | null): Filter {
  return FILTERS.find((option) => option === value) ?? 'all';
}
type Statut = 'ACTIV' | 'INACTIV' | 'RADIAT';
type Reason = 'APPLIED' | 'IDNO_NOT_FOUND' | 'ALREADY_PROCESSED' | 'NO_CHANGE';

// The Registrar as the event found and left it. Since T115 an event changes the
// name and the legal status, never the active flag (CF-12); older rows may
// still show the flag moving, and carry no legal status.
interface McconnectOutcome {
  name: string;
  active: boolean;
  legalStatus?: string | null;
}

interface DevMconnectEvent {
  eventId: string;
  receivedAtIso: string;
  idno: string;
  payload: { name: string; statut: Statut };
  applied: boolean;
  reason: Reason;
  registrarId: string | null;
  before: McconnectOutcome | null;
  after: McconnectOutcome | null;
}

interface McconnectEventsResponse {
  events: DevMconnectEvent[];
  // TC-UC71-01-03: the simulate route answers only in a test environment.
  simulateEnabled?: boolean;
}

interface SimulateForm {
  eventId: string;
  idno: string;
  name: string;
  statut: Statut;
}

function emptyForm(): SimulateForm {
  return { eventId: '', idno: '', name: '', statut: 'ACTIV' };
}

const REASON_VARIANT: Record<
  Reason,
  { semantic: 'success' | 'danger' | 'accent' | 'neutral'; key: string }
> = {
  APPLIED: { semantic: 'success', key: 'mconnect_events.reason.applied' },
  IDNO_NOT_FOUND: { semantic: 'danger', key: 'mconnect_events.reason.idno_not_found' },
  ALREADY_PROCESSED: { semantic: 'accent', key: 'mconnect_events.reason.already_processed' },
  NO_CHANGE: { semantic: 'neutral', key: 'mconnect_events.reason.no_change' },
};

export function McnonnectEventsPage(): JSX.Element {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useCriteriaParams();
  const filter = readFilter(params.get('filter'));
  function setFilter(value: Filter): void {
    const next = new URLSearchParams(params);
    if (value === 'all') next.delete('filter');
    else next.set('filter', value);
    setParams(next);
  }
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<SimulateForm>(emptyForm);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [copiedEventId, setCopiedEventId] = useState<string | null>(null);

  function copyEventId(eventId: string): void {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(eventId).then(
      () => {
        setCopiedEventId(eventId);
        window.setTimeout(() => setCopiedEventId(null), 1500);
      },
      () => {
        // ignore clipboard failure (e.g. denied permission)
      },
    );
  }

  const events = useQuery({
    queryKey: ['admin-mconnect-events', filter],
    queryFn: () =>
      apiClient
        .get<McconnectEventsResponse>(`/admin/mconnect-events?filter=${filter}`)
        .then((r) => r.data),
    refetchInterval: 10_000,
  });
  // Outside a test environment the bff serves no simulate route, so the screen
  // offers no action that could only fail.
  const simulateEnabled = events.data?.simulateEnabled === true;

  const simulate = useMutation({
    mutationFn: (payload: SimulateForm) =>
      apiClient
        .post<DevMconnectEvent>('/admin/mconnect-events/simulate', {
          eventId: payload.eventId.trim() || undefined,
          idno: payload.idno.trim(),
          name: payload.name.trim(),
          statut: payload.statut,
        })
        .then((r) => r.data),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-mconnect-events'] });
      setModalOpen(false);
      setForm(emptyForm());
      // The new row lands in a filtered, paginated table that may not be showing
      // it, so the modal closing is the only thing the reader would otherwise
      // see — and that happens on a refusal too.
      toast({
        variant: 'success',
        title: t('mconnect_events.toast.simulated'),
        text: created.idno,
      });
    },
    // The fallback names this operation — injecting a simulated event, not
    // saving a record — and the code, when the service sends one, wins over it.
    onError: (err) => setSubmitError(t(apiErrorKey(err, 'mconnect_events.error.simulate_failed'))),
  });

  const rows = events.data?.events ?? [];
  const columns: TableColumn[] = [
    { key: 'time', label: t('mconnect_events.column.time') },
    { key: 'eventId', label: t('mconnect_events.column.event_id') },
    { key: 'idno', label: t('mconnect_events.column.idno') },
    { key: 'payload', label: t('mconnect_events.column.payload') },
    { key: 'status', label: t('mconnect_events.column.status') },
    { key: 'diff', label: t('mconnect_events.column.diff') },
  ];
  const canSubmit = form.idno.trim().length > 0 && form.name.trim().length > 0;

  const filters: ReadonlyArray<{ value: Filter; labelKey: string }> = useMemo(
    () => [
      { value: 'all', labelKey: 'mconnect_events.filter.all' },
      { value: 'applied', labelKey: 'mconnect_events.filter.applied' },
      { value: 'skipped', labelKey: 'mconnect_events.filter.skipped' },
    ],
    [],
  );

  // `all` is the register's own default, so it is not a criterion.
  const activeFilters: ActiveFilter[] =
    filter === 'all'
      ? []
      : [
          {
            key: 'status',
            label: t('mconnect_events.column.status'),
            value: t(`mconnect_events.filter.${filter}`),
            onRemove: () => setFilter('all'),
          },
        ];

  function renderDiff(e: DevMconnectEvent): JSX.Element | null {
    if (!e.before || !e.after) return null;
    const parts: string[] = [];
    if (e.before.name !== e.after.name) {
      parts.push(t('mconnect_events.diff.from_to', { before: e.before.name, after: e.after.name }));
    }
    const legalBefore = e.before.legalStatus ?? '';
    const legalAfter = e.after.legalStatus ?? '';
    if (legalBefore !== legalAfter) {
      parts.push(
        t('mconnect_events.diff.legal_status', {
          before: legalBefore ? rsudStatutLabel(t, legalBefore) : '—',
          after: legalAfter ? rsudStatutLabel(t, legalAfter) : '—',
        }),
      );
    }
    if (e.before.active !== e.after.active) {
      parts.push(
        t('mconnect_events.diff.from_to', {
          before: e.before.active
            ? t('mconnect_events.diff.active_yes')
            : t('mconnect_events.diff.active_no'),
          after: e.after.active
            ? t('mconnect_events.diff.active_yes')
            : t('mconnect_events.diff.active_no'),
        }),
      );
    }
    if (parts.length === 0) return null;
    return (
      <span className="simsm-mconnect-events__cell-diff">
        {parts.map((p, i) => (
          <span key={i}>{p}</span>
        ))}
      </span>
    );
  }

  // An empty register keeps its table. The wireframe leaves the head, the
  // frame and the footer standing and puts the message in one centred cell
  // spanning every column (Rapoarte.tsx:107-135); a box in
  // place of the register hides what the register would even hold. Both
  // QueryState branches render this same node, because mud-table draws its
  // `empty` slot on its own once `rows` is [].
  const register = (
    <div className="simsm-mconnect-events__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={rows.map((e) => ({
          id: `${e.eventId}-${e.receivedAtIso}`,
          time: formatDateTime(e.receivedAtIso),
          eventId: e.eventId,
          idno: e.idno,
          payload: `${e.payload.name} ${e.payload.statut}`.trim(),
          status: t(REASON_VARIANT[e.reason].key),
          diff: '',
        }))}
        rowStyle="divided"
        disableSort={true}
        aria-label={t('mconnect_events.table.aria_label')}
      >
        <EmptyRegisterMessage>
          {t(simulateEnabled ? 'mconnect_events.empty' : 'mconnect_events.empty_live')}
        </EmptyRegisterMessage>
        {rows.map((e, index) => {
          const reasonMeta = REASON_VARIANT[e.reason];
          const key = `${e.eventId}-${e.receivedAtIso}`;
          return [
            <span
              key={`time-${key}`}
              slot={`cell-time-${index}`}
              className="simsm-mconnect-events__cell-time"
            >
              {formatDateTime(e.receivedAtIso)}
            </span>,
            <MudButton
              shape="circular"
              key={`event-${key}`}
              slot={`cell-eventId-${index}`}
              variant="strict"
              appearance="text"
              size="sm"
              className={`simsm-mconnect-events__cell-event-id${
                copiedEventId === e.eventId ? ' simsm-mconnect-events__cell-event-id--copied' : ''
              }`}
              title={e.eventId}
              aria-label={t('mconnect_events.event_id.copy_aria')}
              onClick={() => copyEventId(e.eventId)}
            >
              <code>{e.eventId.slice(0, 8)}…</code>
            </MudButton>,
            <code
              key={`idno-${key}`}
              slot={`cell-idno-${index}`}
              className="simsm-mconnect-events__cell-idno"
            >
              {e.idno || (
                <span className="simsm-mconnect-events__cell-empty" aria-label={t('common.empty')}>
                  ·
                </span>
              )}
            </code>,
            <span
              key={`payload-${key}`}
              slot={`cell-payload-${index}`}
              className="simsm-mconnect-events__cell-payload"
            >
              {/* The name is the half of the cell that may be shortened,
                  so it carries the whole value in `title` — the same
                  affordance the event id above it uses. */}
              <span
                className="simsm-mconnect-events__payload-name"
                title={e.payload.name || undefined}
              >
                {e.payload.name || (
                  <span
                    className="simsm-mconnect-events__cell-empty"
                    aria-label={t('common.empty')}
                  >
                    ·
                  </span>
                )}
              </span>
              <MudTag variant="info" label={e.payload.statut} />
            </span>,
            <span key={`status-${key}`} slot={`cell-status-${index}`}>
              <MudTag variant="status" semantic={reasonMeta.semantic} label={t(reasonMeta.key)} />
            </span>,
            <span key={`diff-${key}`} slot={`cell-diff-${index}`}>
              {renderDiff(e) ?? (
                <span
                  className="simsm-mconnect-events__cell-diff-empty"
                  aria-label={t('common.empty')}
                >
                  ·
                </span>
              )}
            </span>,
          ];
        })}
      </MudTable>
    </div>
  );

  return (
    <section className="simsm-page simsm-mconnect-events">
      <PageHeader
        title={t('mconnect_events.title')}
        lede={t('mconnect_events.lede')}
        actions={
          simulateEnabled ? (
            <MudButton
              shape="circular"
              variant="primary"
              size="md"
              onClick={() => {
                setSubmitError(null);
                setForm(emptyForm());
                setModalOpen(true);
              }}
            >
              <MudIcon slot="icon-start" name="plus-large" size={20} />
              {t('mconnect_events.action.simulate')}
            </MudButton>
          ) : null
        }
      />

      <FilterBar
        active={activeFilters}
        onReset={() => setFilter('all')}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('mconnect_events.filter.aria_group_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <label className="simsm-filter-bar__field">
            <span className="simsm-filter-bar__field-label">
              {t('mconnect_events.column.status')}
            </span>
            <MudSelect
              size="medium"
              value={filter}
              aria-label={t('mconnect_events.column.status')}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setFilter(e.detail.value as Filter)
              }
            >
              {filters.map((f) => (
                <option key={f.value} value={f.value}>
                  {t(f.labelKey)}
                </option>
              ))}
            </MudSelect>
          </label>
        }
      />

      <QueryState query={events} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>

      <MudModal
        closeLabel={t('common.close')}
        open={modalOpen && simulateEnabled}
        size="md"
        titleText={t('mconnect_events.modal.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 —
        // seven AGE components emit `mudClose` as a bubbling, composed event, so
        // a descendant closing inside this modal reaches this handler too.
        // Without the guard the Statut picker below takes the form down the
        // moment an option is chosen.
        onMudClose={onModalDismiss(() => setModalOpen(false))}
      >
        <div className="simsm-mconnect-events__form">
          {submitError ? (
            <MudInfoBox emphasis="strong" role="alert" variant="error">
              {submitError}
            </MudInfoBox>
          ) : null}
          {/* The fields carry their own labels: a light-DOM <label> around a
              mud-text-input reaches no control, the native input lives in the
              shadow root. */}
          <MudTextInput
            size="md"
            label={t('mconnect_events.modal.idno')}
            value={form.idno}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, idno: e.detail.value })
            }
          />
          <MudTextInput
            size="md"
            label={t('mconnect_events.modal.name')}
            value={form.name}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, name: e.detail.value })
            }
          />
          <MudSelect
            size="medium"
            label={t('mconnect_events.modal.statut')}
            value={form.statut}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, statut: e.detail.value as Statut })
            }
          >
            <option value="ACTIV">{t('mconnect_events.modal.statut.activ')}</option>
            <option value="INACTIV">{t('mconnect_events.modal.statut.inactiv')}</option>
            <option value="RADIAT">{t('mconnect_events.modal.statut.radiat')}</option>
          </MudSelect>
          <MudTextInput
            size="md"
            label={t('mconnect_events.modal.event_id')}
            value={form.eventId}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, eventId: e.detail.value })
            }
          />
        </div>
        <div slot="actions" className="simsm-mconnect-events__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setModalOpen(false)}
          >
            {t('mconnect_events.modal.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={!canSubmit || simulate.isPending}
            onClick={() => {
              setSubmitError(null);
              simulate.mutate(form);
            }}
          >
            {t('mconnect_events.modal.submit')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
